<template>
  <v-card class="dialog px-5">
    <v-file-input
      v-model="archivosSeleccionados"
      prepend-icon="mdi-camera"
      accept="image/png, image/jpeg, image/bmp"
      :label="multiple ? 'Seleccionar imágenes' : 'Seleccionar imagen'"
      :placeholder="multiple ? 'Puedes elegir varias a la vez' : 'Sube tu foto'"
      :multiple="multiple"
      variant="outlined"
      :disabled="procesando || subiendo"
      @update:model-value="onImageSelected"
    />

    <!-- Modo multiple: cuadricula de miniaturas -->
    <template v-if="multiple">
      <div
        v-if="procesando"
        class="estado-procesando"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="22"
        />
        <span class="ml-2">Preparando imagenes...</span>
      </div>

      <div
        v-if="imagenes.length > 0"
        class="cabecera-lista"
      >
        <span>{{ imagenes.length }} imagen(es) lista(s)</span>
        <v-btn
          variant="text"
          size="x-small"
          :disabled="subiendo"
          @click="quitarTodas"
        >
          Quitar todas
        </v-btn>
      </div>

      <div
        v-if="imagenes.length > 0"
        class="cuadricula"
      >
        <div
          v-for="(imagen, indice) in imagenes"
          :key="indice"
          class="miniatura"
        >
          <v-img
            :src="imagen.previsualizacion"
            cover
            class="imagen-miniatura"
          />
          <v-btn
            class="bg-red boton-quitar"
            size="x-small"
            :disabled="subiendo"
            @click="quitarImagen(indice)"
          >
            <v-icon size="15">mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </template>

    <!-- Modo simple: se conserva la vista previa unica de siempre -->
    <div
      v-else-if="showImage"
      style="margin-top: 15px;text-align: center;"
    >
      <v-img
        :src="previewImage"
        alt="Vista previa"
        style="max-width: 100%; max-height: 300px; border: 1px solid #ccc;"
      />
    </div>

    <v-btn
      class="bg-blue mt-3"
      :loading="isUploading || subiendo"
      :disabled="!hayAlgoQueSubir || isUploading || subiendo || procesando"
      @click="uploadImage"
    >
      <v-icon class="mr-1">mdi-upload</v-icon>
      {{ textoBoton }}
    </v-btn>

    <div
      v-if="progreso"
      class="texto-progreso"
    >
      {{ progreso }}
    </div>

    <v-alert
      v-if="uploadSuccess"
      type="success"
      variant="outlined"
      class="mt-3"
      closable
    >
      Image uploaded successfully!
    </v-alert>

    <v-alert
      v-if="uploadError"
      type="error"
      variant="outlined"
      class="mt-3"
      closable
    >
      Error uploading image. Please try again.
    </v-alert>
  </v-card>
</template>
<script>
/*
  onImageSelected(img)   -> base64 unico cuando multiple es false
  onImageSelected([img]) -> arreglo de base64 cuando multiple es true

  La subida la hace el componente padre, que es el que conoce la API. El padre
  puede reportar su avance con las props subiendo y progreso.
*/

const LADO_MAXIMO = 1200;
const CALIDAD_JPEG = 0.75;

export default {
  name: 'EntryLoalImage',
  props: {
    // Permite elegir varias imagenes de una sola vez y las va acumulando
    multiple: {
      type: Boolean,
      default: false
    },
    // Lo controla el padre mientras sube, para bloquear el formulario
    subiendo: {
      type: Boolean,
      default: false
    },
    // Texto de avance del padre, por ejemplo "Subiendo 2 de 5"
    progreso: {
      type: String,
      default: ''
    }
  },
  emits: ["onImageSelected"],
  data() {
    return {
      // Vuetify entrega File[] cuando el input es multiple y File cuando no
      archivosSeleccionados: this.multiple ? [] : null,
      // Modo multiple: [{ base64, previsualizacion }]
      imagenes: [],
      procesando: false,
      // Modo simple: se mantiene tal cual estaba
      selectedImageBase64: null,
      previewImage: null,
      showImage: false,
      isUploading: false,
      uploadSuccess: false,
      uploadError: false
    };
  },
  computed: {
    hayAlgoQueSubir() {
      return this.multiple
        ? this.imagenes.length > 0
        : !!this.selectedImageBase64;
    },
    textoBoton() {
      if (!this.multiple) {
        return 'Subir imagen';
      }
      if (this.imagenes.length === 0) {
        return 'Subir imágenes';
      }
      return this.imagenes.length === 1
        ? 'Subir 1 imagen'
        : `Subir ${this.imagenes.length} imágenes`;
    }
  },
  methods: {
    onImageSelected(payload) {
      const archivos = this.normalizarArchivos(payload);

      if (archivos.length === 0) {
        if (!this.multiple) {
          this.showImage = false;
          this.selectedImageBase64 = null;
        }
        return;
      }

      if (this.multiple) {
        this.procesarVarios(archivos);
      } else {
        this.procesarUno(archivos[0]);
      }
    },
    /**
     * v-file-input entrega File, File[] o null segun la version de Vuetify, y
     * si el evento llega crudo viene en target.files. Se cubren los tres casos.
     */
    normalizarArchivos(payload) {
      if (!payload) {
        return [];
      }
      if (payload.target && payload.target.files) {
        return Array.from(payload.target.files);
      }
      if (Array.isArray(payload)) {
        return payload.filter(archivo => !!archivo);
      }
      return [payload];
    },
    procesarUno(archivo) {
      this.comprimir(archivo)
        .then(comprimida => {
          this.previewImage = comprimida;
          this.showImage = true;
          this.selectedImageBase64 = comprimida.split(",")[1];
        })
        .catch(error => {
          console.error("No se pudo leer la imagen:", error);
          this.uploadError = true;
        });
    },
    procesarVarios(archivos) {
      this.procesando = true;
      this.uploadError = false;

      // Se acumulan: asi se pueden ir agregando imagenes de carpetas distintas
      // sin perder las que ya estaban elegidas.
      Promise.all(archivos.map(archivo => this.comprimir(archivo)))
        .then(comprimidas => {
          comprimidas.forEach(comprimida => {
            this.imagenes.push({
              previsualizacion: comprimida,
              base64: comprimida.split(",")[1]
            });
          });
        })
        .catch(error => {
          console.error("No se pudieron leer las imagenes:", error);
          this.uploadError = true;
        })
        .finally(() => {
          this.procesando = false;
          // Se limpia el input para poder volver a elegir mas archivos
          this.archivosSeleccionados = [];
        });
    },
    /** Reduce la imagen a LADO_MAXIMO y la devuelve como data URL jpeg. */
    comprimir(archivo) {
      return new Promise((resolve, reject) => {
        const lector = new FileReader();

        lector.onerror = () => reject(new Error("Error leyendo el archivo"));
        lector.onload = (evento) => {
          const imagen = new Image();

          imagen.onerror = () => reject(new Error("Archivo de imagen invalido"));
          imagen.onload = () => {
            let ancho = imagen.width;
            let alto = imagen.height;

            if (ancho > LADO_MAXIMO || alto > LADO_MAXIMO) {
              if (ancho > alto) {
                alto = Math.round(alto * LADO_MAXIMO / ancho);
                ancho = LADO_MAXIMO;
              } else {
                ancho = Math.round(ancho * LADO_MAXIMO / alto);
                alto = LADO_MAXIMO;
              }
            }

            const lienzo = document.createElement("canvas");
            lienzo.width = ancho;
            lienzo.height = alto;
            lienzo.getContext("2d").drawImage(imagen, 0, 0, ancho, alto);

            resolve(lienzo.toDataURL("image/jpeg", CALIDAD_JPEG));
          };
          imagen.src = evento.target.result;
        };

        lector.readAsDataURL(archivo);
      });
    },
    quitarImagen(indice) {
      this.imagenes.splice(indice, 1);
    },
    quitarTodas() {
      this.imagenes = [];
    },
    /** Limpia la seleccion. Lo llama el padre cuando la subida termino bien. */
    limpiar() {
      this.imagenes = [];
      this.archivosSeleccionados = this.multiple ? [] : null;
      this.selectedImageBase64 = null;
      this.previewImage = null;
      this.showImage = false;
    },
    uploadImage() {
      if (!this.hayAlgoQueSubir) {
        return;
      }

      this.uploadSuccess = false;
      this.uploadError = false;

      if (this.multiple) {
        this.$emit('onImageSelected', this.imagenes.map(imagen => imagen.base64));
        return;
      }

      this.$emit('onImageSelected', this.selectedImageBase64);
    }
  }
}

</script>
<style scoped>
.dialog {
  padding: 20px;
  width: 100%;
}
.estado-procesando {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: #777;
  padding: 8px 0;
}
.cabecera-lista {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #777;
  margin-top: 4px;
}
.cuadricula {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  margin-top: 6px;
  max-height: 320px;
  overflow-y: auto;
}
.miniatura {
  position: relative;
  border: 1px solid #ddd;
  border-radius: 6px;
  overflow: hidden;
}
.imagen-miniatura {
  width: 100%;
  height: 90px;
}
.boton-quitar {
  position: absolute;
  top: 3px;
  right: 3px;
  min-width: 0;
  padding: 0 5px;
}
.texto-progreso {
  text-align: center;
  font-size: 0.82rem;
  color: #1976d2;
  margin-top: 8px;
}

@media screen and (min-width: 600px) {
  .cuadricula {
    grid-template-columns: repeat(4, 1fr);
  }
}
</style>
